import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Horror Film",
  "Live Event / Experience",
  "Concert / Stage Visuals",
  "Brand Film",
  "Other",
];

const TIMELINES = ["ASAP", "1-2 Weeks", "1 Month", "2-3 Months", "Flexible"];

const BUDGETS = [
  "Under $1,000",
  "$1,000 - $3,000",
  "$3,000 - $7,000",
  "$7,000+",
  "Not sure yet",
];

const initial = {
  name: "",
  email: "",
  project_type: "",
  timeline: "",
  budget_range: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.project_type || !form.message.trim()) {
      toast.error("Please fill in your name, email, project type, and description.");
      return;
    }
    setLoading(true);
    try {
      // Map to backend payload (subject = project_type for legacy compatibility)
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.project_type,
        message: form.message,
        project_type: form.project_type,
        timeline: form.timeline,
        budget_range: form.budget_range,
      });
      toast.success("The ritual has begun. We'll be in touch by candlelight.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = loading
    ? "OPENING THE GATE..."
    : pressed || hovered
    ? "STEP THROUGH"
    : "BEGIN THE RITUAL";

  return (
    <div className="page-enter max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="contact-page">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="overline">Open the Crypt</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4" data-testid="contact-heading">
            Tell us what's <em style={{ color: "var(--gl-amber)" }}>haunting</em> you.
          </h1>
          <p className="mt-6 max-w-md" style={{ color: "var(--gl-muted)" }}>
            Briefs, half-ideas, unfinished scripts, the fragment you dreamed at 3am — all welcome.
            We reply within three working nights.
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <div>
              <div className="overline mb-1">Correspondence</div>
              <a href="mailto:hello@gravelightstudio.com" className="gl-link" data-testid="contact-email">
                hello@gravelightstudio.com
              </a>
            </div>
            <div>
              <div className="overline mb-1">Phone</div>
              <a href="tel:+16784477382" className="gl-link" data-testid="contact-phone">
                678-447-7382
              </a>
            </div>
            <div>
              <div className="overline mb-1">Lair</div>
              <div style={{ color: "var(--gl-muted)" }}>Atlanta, Georgia · remote worldwide</div>
            </div>
            <div>
              <div className="overline mb-1">Hours</div>
              <div style={{ color: "var(--gl-muted)" }}>Dusk to dawn, mostly.</div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="gl-card p-8" data-testid="contact-form">
          <div className="space-y-5">
            <div>
              <label className="overline block mb-2">Your name</label>
              <input
                className="gl-input"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Ada Blackwood"
                data-testid="contact-input-name"
              />
            </div>

            <div>
              <label className="overline block mb-2">Email</label>
              <input
                className="gl-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="ada@midnight.co"
                data-testid="contact-input-email"
              />
            </div>

            <div>
              <label className="overline block mb-2">Project type</label>
              <select
                className="gl-input"
                value={form.project_type}
                onChange={update("project_type")}
                data-testid="contact-input-project-type"
              >
                <option value="">Select a project type…</option>
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="overline block mb-2">
                Timeline <span style={{ opacity: 0.6 }}>(Optional)</span>
              </label>
              <select
                className="gl-input"
                value={form.timeline}
                onChange={update("timeline")}
                data-testid="contact-input-timeline"
              >
                <option value="">Select a timeline…</option>
                {TIMELINES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="overline block mb-2">
                Budget range <span style={{ opacity: 0.6 }}>(Optional)</span>
              </label>
              <select
                className="gl-input"
                value={form.budget_range}
                onChange={update("budget_range")}
                data-testid="contact-input-budget"
              >
                <option value="">Select a budget range…</option>
                {BUDGETS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="overline block mb-2">Project description</label>
              <textarea
                rows={6}
                className="gl-input resize-none"
                value={form.message}
                onChange={update("message")}
                placeholder="Whisper the brief..."
                data-testid="contact-input-message"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex flex-col items-center justify-center gap-1 py-4 px-6 transition-all duration-300"
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => { setHovered(false); setPressed(false); }}
              onMouseDown={() => setPressed(true)}
              onMouseUp={() => setPressed(false)}
              onFocus={() => setHovered(true)}
              onBlur={() => setHovered(false)}
              style={{
                border: "1px solid var(--gl-amber)",
                color: "var(--gl-amber)",
                background: hovered ? "rgba(251,191,36,0.1)" : "transparent",
                boxShadow: hovered ? "0 0 24px rgba(251,191,36,0.25)" : "none",
                borderRadius: 2,
                cursor: loading ? "wait" : "pointer",
                transform: hovered ? "translateY(-1px)" : "none",
              }}
              data-testid="contact-submit-button"
            >
              <span
                className="font-body"
                style={{
                  fontSize: "0.95rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                {buttonLabel}
              </span>
              <span
                className="font-body"
                style={{
                  fontSize: "0.62rem",
                  letterSpacing: "0.45em",
                  textTransform: "uppercase",
                  opacity: 0.55,
                }}
                data-testid="contact-submit-subtext"
              >
                Submit your inquiry
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
